import { useEffect, useMemo, useState } from 'react'
import { ChevronLeft, ChevronRight, Sparkles } from 'lucide-react'
import { fetchProducts } from '../data/api'
import { ProductCard } from './ProductCard'
import { SkeletonGrid } from './SkeletonGrid'

function shuffle(array) {
  const copy = [...array]
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[copy[i], copy[j]] = [copy[j], copy[i]]
  }
  return copy
}

export function FeaturedProducts({ segment, onSelectProduct, onAdd }) {
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(true)
  const [offset, setOffset] = useState(0)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setOffset(0)

    fetchProducts(segment)
      .then((res) => {
        if (cancelled) return
        if (res.success) setProducts(shuffle(res.data.products))
      })
      .catch((err) => {
        if (!cancelled) console.error('Featured products load failed:', err)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [segment])

  const visible = useMemo(() => {
    if (products.length <= 4) return products
    return Array.from({ length: 4 }, (_, i) => products[(offset + i) % products.length])
  }, [products, offset])

  const canSlide = products.length > 4

  const prev = () => setOffset((o) => (o - 1 + products.length) % products.length)
  const next = () => setOffset((o) => (o + 1) % products.length)

  return (
    <section className="bg-brand-shell px-4 py-16 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="mb-8 flex items-end justify-between">
          <div>
            <h2 className="heading-accent inline-flex items-center gap-2 font-display text-2xl font-bold text-brand-ink">
              <Sparkles className="h-6 w-6 text-brand-gold" />
              Featured products
            </h2>
            <p className="mt-2 text-sm text-brand-slate">
              A rotating selection from the {segment === 'retail' ? 'retail' : 'wholesale'} catalog at Alpha Import Export — all sourced directly from verified producers.
            </p>
          </div>
          {canSlide && (
            <div className="flex gap-2">
              <button
                onClick={prev}
                className="rounded-full bg-white p-2 text-brand-navy shadow-sm transition-all hover:bg-brand-gold hover:text-brand-navy"
                aria-label="Previous featured products"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                onClick={next}
                className="rounded-full bg-white p-2 text-brand-navy shadow-sm transition-all hover:bg-brand-gold hover:text-brand-navy"
                aria-label="Next featured products"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          )}
        </div>

        {loading ? (
          <SkeletonGrid count={4} />
        ) : visible.length === 0 ? (
          <p className="text-center text-sm text-brand-slate">No featured products available.</p>
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 lg:gap-6">
            {visible.map((product) => (
              <ProductCard
                key={`${product.id}-${offset}`}
                product={product}
                segment={segment}
                onSelect={onSelectProduct}
                onAdd={(p) => onAdd(p, segment === 'retail' ? 1 : p.moq || 1)}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  )
}
